import React from 'react';

/**
 * The band the observer watches for a section: the section itself and, reaching 600px above it,
 * how far ahead of the visible area it starts loading. Any part of it on screen counts, so a
 * section the reader dragged the scrollbar past and sees only the lower half of loads too.
 *
 * A band rather than the observer's `rootMargin`: the margin only applies when the observer's root
 * is a document of the frame's own origin and no scroll container clips in between, and this frame
 * is cross-origin inside its host and scrolls inside the body or a screen's own container. A band
 * is clipped by whichever container scrolls, exactly like the section itself, so the look-ahead
 * holds in every one of them.
 */
const LOOK_AHEAD_BAND_CLASSES = '-top-[600px] bottom-0';

/**
 * True once the element has come into the visible area, and true from then on. A section further
 * down a screen asks for its data only when the reader is about to see it, so opening the screen
 * does not start every section's requests at once. Without an IntersectionObserver the element
 * counts as near from the start, so nothing waits on an observer that cannot fire.
 *
 * Returns the ref callback to put on the element to watch: a callback rather than a ref object,
 * so an element that mounts after the hook's first render (a screen still showing its skeleton)
 * is observed as soon as it appears.
 */
export function useNearViewport(): [boolean, (element: HTMLElement | null) => void] {
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const [near, setNear] = React.useState(() => typeof IntersectionObserver === 'undefined');

  React.useEffect(() => {
    if (near || !element || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setNear(true);
        observer.disconnect();
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, near]);

  return [near, setElement];
}

/**
 * The band `useNearViewport` watches for a section: invisible, covering the section and the
 * look-ahead above it. The section's own wrapper must be `relative`.
 */
export function NearViewportBand({ bandRef }: { bandRef: (element: HTMLElement | null) => void }) {
  return <div ref={bandRef} aria-hidden="true" className={`pointer-events-none absolute inset-x-0 ${LOOK_AHEAD_BAND_CLASSES}`} />;
}

/**
 * Renders a section only once it has come near the visible area; until then a block of the
 * section's usual height holds its place. A section that turns out to have nothing to show gives
 * that room back while it is still the look-ahead's distance below the reader.
 */
export function LoadWhenNearViewport({ children, placeholderClassName }: { children: React.ReactNode; placeholderClassName: string }) {
  const [near, bandRef] = useNearViewport();

  return near ? (
    <>{children}</>
  ) : (
    <div aria-hidden="true" className={`relative ${placeholderClassName}`}>
      <NearViewportBand bandRef={bandRef} />
    </div>
  );
}
